import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/api";
import { CoachPlan, money } from "./PricingSettings";

export interface PlanChoice {
  startingPlanId: string;
  offeredPlanIds: string[];
}

/**
 * The coach choosing, as they add an athlete, which plan they start on and which others their family may
 * switch to (27 Sep debug notes item 12).
 *
 * Every other plan starts ticked, which is what a family could see before this existed; unticking one is
 * the coach narrowing it. Reports the finished choice upwards rather than its raw state, so the form only
 * ever sends something the server will accept -- and nothing at all while there are no plans to choose.
 */
export function PlanChoiceFields({
  idPrefix,
  onChange,
}: {
  idPrefix: string;
  onChange: (choice: PlanChoice | null) => void;
}) {
  const query = useQuery({
    queryKey: ["billing", "plans"],
    queryFn: () => apiGet<{ plans: CoachPlan[] }>("/billing/plans"),
  });
  const plans = (query.data?.plans ?? []).filter((plan) => !plan.archived).sort((a, b) => a.sortOrder - b.sortOrder);

  const [startingPlanId, setStartingPlanId] = useState<string | null>(null);
  const [excluded, setExcluded] = useState<string[]>([]);

  const starting = startingPlanId ?? plans[0]?.id ?? null;
  const others = plans.filter((plan) => plan.id !== starting);
  const offeredKey = others.filter((plan) => !excluded.includes(plan.id)).map((plan) => plan.id).join(",");

  useEffect(() => {
    onChange(starting ? { startingPlanId: starting, offeredPlanIds: offeredKey ? offeredKey.split(",") : [] } : null);
    // `onChange` is the parent's setter; re-running on its identity would loop on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [starting, offeredKey]);

  if (plans.length === 0) return null;

  return (
    <>
      <label htmlFor={`${idPrefix}-starting-plan`}>Starting plan</label>
      <select id={`${idPrefix}-starting-plan`} value={starting ?? ""} onChange={(e) => setStartingPlanId(e.target.value)}>
        {plans.map((plan) => (
          <option key={plan.id} value={plan.id}>
            {plan.name} — {money(plan.priceCents)}/mo
          </option>
        ))}
      </select>

      {others.length > 0 && (
        <>
          <p className="form-label">Other plans they can switch to</p>
          <div className="checkbox-list">
            {others.map((plan) => (
              <label key={plan.id} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!excluded.includes(plan.id)}
                  onChange={(e) =>
                    setExcluded(e.target.checked ? excluded.filter((id) => id !== plan.id) : [...excluded, plan.id])
                  }
                />
                {plan.name}
                {plan.requiresApproval && <span className="badge">Needs your approval</span>}
              </label>
            ))}
          </div>
        </>
      )}
    </>
  );
}
